<template>
  <a-modal title="Associate Document" width="600px" :visible="visible" :destroy-on-close="true" @cancel="closeModal">
    <template v-if="template">
      <div class="flex flex-column gap-2">
        <div class="time-wrapper">
          <a-descriptions :column="1" size="small" class="p-0">
            <a-descriptions-item label="Name">{{ template?.name }} </a-descriptions-item>
            <a-descriptions-item label="Type">{{ template?.type?.name }} </a-descriptions-item>
            <a-descriptions-item label="Description">{{ template?.type?.description ?? ' - ' }} </a-descriptions-item>
          </a-descriptions>
        </div>
      </div>

      <a-form-model
        layout="horizontal"
        ref="form"
        class="flex gap-2 flex-column"
        :model="form"
        :rules="rules"
        :label-col="{ span: 8 }"
        :wrapper-col="{ span: 16 }"
      >
        <a-form-model-item
          v-if="template.has_number"
          :colon="false"
          label-align="left"
          label="Document Number"
          prop="number"
        >
          <a-input v-model="form.number" placeholder="Document Number" />
        </a-form-model-item>
        <a-form-model-item
          v-if="template.can_expire"
          :colon="false"
          label-align="left"
          label="Expiration Date"
          prop="expiration_date"
        >
          <a-date-picker
            format="MM/DD/YYYY"
            placeholder="Expiration Date"
            v-model="form.expiration_date"
            valueFormat="YYYY-MM-DD"
          />
        </a-form-model-item>
        <a-form-model-item prop="files" :colon="false" label-align="left" label="Attachments">
          <document-attachments :with-title="false" :files="form.files" @save="saveAttachmentsIds" />
        </a-form-model-item>
      </a-form-model>
    </template>

    <template slot="footer">
      <div class="flex justify-content-between">
        <a-button @click="closeModal"> Cancel </a-button>
        <a-button icon="save" type="primary" @click="save" :loading="loading"> Save </a-button>
      </div>
    </template>
  </a-modal>
</template>

<script>
import { Button, FormModel, Modal, Input, notification, DatePicker, Descriptions } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'
import ShiftFormAttachments from '@/components/form-attachments.vue'
import moment from 'moment-timezone'

const { mapActions: documentActions } = createNamespacedHelpers('document')

const initialForm = {
  number: undefined,
  expiration_date: undefined,
  files: []
}

const initialRules = {
  number: [
    {
      required: true,
      message: 'Number can not be empty',
      trigger: 'change'
    }
  ],
  expiration_date: [
    {
      required: true,
      message: 'Expiration date can not be empty',
      trigger: 'change'
    }
  ],
  files: [
    {
      required: true,
      message: 'Files can not be empty',
      trigger: 'change',
      type: 'array'
    }
  ]
}

export default {
  components: {
    'a-button': Button,
    'a-modal': Modal,
    'a-date-picker': DatePicker,
    'a-form-model': FormModel,
    'a-form-model-item': FormModel.Item,
    'a-input': Input,
    'a-descriptions': Descriptions,
    'a-descriptions-item': Descriptions.Item,
    'document-attachments': ShiftFormAttachments
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    associate_id: {
      type: Number,
      default: undefined,
      required: true
    },
    template_id: {
      type: Number,
      default: undefined
    }
  },
  data() {
    return {
      visiblePasswordModal: false,
      loading: false,

      form: { ...initialForm },
      rules: {
        ...initialRules
      },

      template: undefined,
      document: undefined,
      attachmentsIds: []
    }
  },
  watch: {
    template_id: {
      handler(newVal) {
        if (newVal && !this.document_id) this.fetchDocumentTemplate()
      },
      immediate: true
    }
  },

  methods: {
    ...documentActions(['createAssociateDocumentTemplate', 'getAssociateTemplate', 'getAssociateDocument']),

    closeModal() {
      this.$emit('update:visible', false)
      this.form = { ...initialForm }
    },

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    async saveAttachmentsIds(ids) {
      this.attachmentsIds = ids
    },

    save() {
      this.form.files = this.attachmentsIds

      this.$refs.form.validate(async valid => {
        if (!valid) return

        this.loading = true

        try {
          const payload = {
            document: {
              ...this.form,
              id: -1,
              name: this.template.name,
              description: this.template.description,
              type: this.template.type.id,
              files: this.attachmentsIds,
              expiration_date: !this.template.can_expire
                ? undefined
                : moment(this.form.expiration_date).utc().startOf('day').toISOString(),
              number: !this.template.has_number ? undefined : this.form.number,
              created_at: moment().utc().startOf('day').toISOString(),
              status: 'accepted',
              template_id: this.template_id
            },
            associate_id: this.associate_id
          }

          const document = await this.createAssociateDocumentTemplate(payload)

          this.showNotification('success', 'Success', 'Associate document has been created successfully.')

          this.$emit('save', document)

          this.closeModal()
        } catch (error) {
          this.showNotification('error', 'Error', error.message || error)
        } finally {
          this.loading = false
        }
      })
    },

    async fetchDocumentTemplate() {
      this.loading = true
      try {
        const template = await this.getAssociateTemplate({
          template_id: this.template_id
        })

        this.template = template
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.ant-row {
  display: flex;
  margin: 0 !important;
}

.ant-form {
  display: flex;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
}

.ant-calendar-picker {
  width: 100%;
}

.ant-checkbox-wrapper + .ant-checkbox-wrapper {
  margin: 0;
}
</style>
